import { useEffect, useState, useCallback, useMemo } from 'react'
import { Card, CardContent } from '../components/ui/card'
import {
    Table,
    TableHeader,
    TableBody,
    TableRow,
    TableHead,
    TableCell,
} from '../components/ui/table'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { adminApi } from '../services/adminApi'
import { Search, ArrowUpRight, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

type StatusFilter = 'all' | 'pending' | 'accepted'
type RangeFilter = '7d' | '30d' | 'all'

type ReferralsResponse = Awaited<ReturnType<typeof adminApi.getReferrals>>
type ReferralRow = ReferralsResponse['items'][number]

const RANGE_TO_SINCE: Record<RangeFilter, () => string | undefined> = {
    '7d': () => new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    '30d': () => new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    all: () => undefined,
}

export default function ReferralsPage() {
    const [items, setItems] = useState<ReferralRow[]>([])
    const [loading, setLoading] = useState(true)
    const [pagination, setPagination] = useState({ page: 1, pageSize: 50, total: 0, pages: 0 })
    const [metrics, setMetrics] = useState({
        totalSent: 0,
        totalAccepted: 0,
        totalPending: 0,
        conversionRate: 0,
    })
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
    const [range, setRange] = useState<RangeFilter>('all')
    const [search, setSearch] = useState('')
    const [searchInput, setSearchInput] = useState('')
    const [revokingId, setRevokingId] = useState<string | null>(null)

    const fetchData = useCallback(async () => {
        try {
            setLoading(true)
            const data = await adminApi.getReferrals({
                page: pagination.page,
                pageSize: pagination.pageSize,
                status: statusFilter === 'all' ? undefined : statusFilter,
                search: search || undefined,
                since: RANGE_TO_SINCE[range](),
            })
            setItems(data.items)
            setPagination(data.pagination)
            setMetrics(data.metrics)
        } catch (error) {
            console.error('Failed to fetch referrals:', error)
        } finally {
            setLoading(false)
        }
    }, [pagination.page, pagination.pageSize, statusFilter, search, range])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    const resetPage = useCallback(() => {
        setPagination(p => (p.page === 1 ? p : { ...p, page: 1 }))
    }, [])

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault()
        resetPage()
        setSearch(searchInput.trim())
    }

    const handleRevoke = async (id: string) => {
        if (revokingId) return
        const ok = window.confirm('Revoke this pending referral? This frees up the slot for the referrer.')
        if (!ok) return
        setRevokingId(id)
        try {
            await adminApi.revokeReferral(id)
            toast.success('Referral revoked')
            setItems(prev => prev.filter(row => row.id !== id))
            void fetchData()
        } catch (error: any) {
            const msg = error?.response?.data?.error ?? 'Failed to revoke'
            toast.error(msg)
        } finally {
            setRevokingId(null)
        }
    }

    const conversionPct = useMemo(
        () => `${(metrics.conversionRate * 100).toFixed(1)}%`,
        [metrics.conversionRate]
    )

    return (
        <div className="p-6 space-y-6 max-w-[1400px] mx-auto">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <h1 className="text-2xl font-semibold tracking-tight">Referrals</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Invites sent through the in-product referral flow.
                    </p>
                </div>
                <div className="flex gap-1.5 p-1 rounded-lg bg-muted/60">
                    {(['7d', '30d', 'all'] as const).map(r => (
                        <button
                            key={r}
                            type="button"
                            onClick={() => {
                                resetPage()
                                setRange(r)
                            }}
                            className={`px-3 h-8 rounded-md text-xs font-medium transition-colors ${
                                range === r
                                    ? 'bg-background text-foreground shadow-sm'
                                    : 'text-muted-foreground hover:text-foreground'
                            }`}
                        >
                            {r === '7d' ? 'Last 7 days' : r === '30d' ? 'Last 30 days' : 'All time'}
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricCard label="Total sent" value={metrics.totalSent} accent="neutral" />
                <MetricCard label="Pending" value={metrics.totalPending} accent="amber" />
                <MetricCard label="Accepted" value={metrics.totalAccepted} accent="emerald" />
                <MetricCard label="Conversion" value={conversionPct} accent="brand" />
            </div>

            <Card>
                <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                        <h2 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">
                            All referrals
                        </h2>
                        <form onSubmit={handleSearch} className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/70 pointer-events-none" />
                            <Input
                                value={searchInput}
                                onChange={e => setSearchInput(e.target.value)}
                                placeholder="Search referrer or recipient email…"
                                className="w-72 h-9 pl-8"
                            />
                        </form>
                    </div>

                    <div className="flex items-center flex-wrap gap-x-2 gap-y-2 mb-4 text-xs">
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-medium mr-1">
                            Status
                        </span>
                        {(['all', 'pending', 'accepted'] as const).map(s => (
                            <FilterPill
                                key={s}
                                active={statusFilter === s}
                                onClick={() => { resetPage(); setStatusFilter(s) }}
                            >
                                {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                            </FilterPill>
                        ))}
                    </div>

                    <div className="border rounded-md overflow-hidden">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[28%]">Referrer</TableHead>
                                    <TableHead className="w-[28%]">Recipient</TableHead>
                                    <TableHead className="w-[12%]">Status</TableHead>
                                    <TableHead className="w-[13%]">Sent</TableHead>
                                    <TableHead className="w-[13%]">Accepted</TableHead>
                                    <TableHead className="w-[6%] text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                                            Loading…
                                        </TableCell>
                                    </TableRow>
                                ) : items.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                                            No referrals match these filters.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    items.map(row => (
                                        <ReferralTableRow
                                            key={row.id}
                                            row={row}
                                            onRevoke={handleRevoke}
                                            revoking={revokingId === row.id}
                                        />
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    {pagination.pages > 1 && (
                        <div className="flex items-center justify-between mt-4 text-xs text-muted-foreground">
                            <span>
                                Page {pagination.page} of {pagination.pages} · {pagination.total} total
                            </span>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={pagination.page <= 1}
                                    onClick={() =>
                                        setPagination(p => ({ ...p, page: Math.max(1, p.page - 1) }))
                                    }
                                >
                                    Previous
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={pagination.page >= pagination.pages}
                                    onClick={() =>
                                        setPagination(p => ({ ...p, page: Math.min(pagination.pages, p.page + 1) }))
                                    }
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    )
}

const ACCENT_BAR: Record<'neutral' | 'amber' | 'emerald' | 'brand', string> = {
    neutral: 'bg-foreground/40',
    amber: 'bg-amber-500',
    emerald: 'bg-emerald-500',
    brand: 'bg-orange-500',
}

function MetricCard({
    label,
    value,
    accent,
}: {
    label: string
    value: number | string
    accent: keyof typeof ACCENT_BAR
}) {
    return (
        <Card className="overflow-hidden">
            <CardContent className="pt-6 pb-5 relative">
                <div className={`absolute left-0 top-0 bottom-0 w-[3px] ${ACCENT_BAR[accent]}`} />
                <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-medium">
                    {label}
                </p>
                <p className="text-3xl font-semibold tracking-tight mt-1.5 tabular-nums">
                    {value}
                </p>
            </CardContent>
        </Card>
    )
}

function ReferralTableRow({
    row,
    onRevoke,
    revoking,
}: {
    row: ReferralRow
    onRevoke: (id: string) => void
    revoking: boolean
}) {
    return (
        <TableRow>
            <TableCell>
                <div className="flex flex-col min-w-0">
                    <span className="font-medium truncate">{row.referrer.name ?? row.referrer.email}</span>
                    {row.referrer.name && (
                        <span className="text-xs text-muted-foreground truncate">
                            {row.referrer.email}
                        </span>
                    )}
                </div>
            </TableCell>
            <TableCell>
                <div className="min-w-0">
                    <span className="truncate block">{row.email}</span>
                    {row.claimedBy && row.claimedBy.email !== row.email && (
                        <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1 mt-0.5">
                            <ArrowUpRight className="w-3 h-3" />
                            claimed by {row.claimedBy.email}
                        </span>
                    )}
                </div>
            </TableCell>
            <TableCell>
                <StatusBadge status={row.status} />
            </TableCell>
            <TableCell className="text-muted-foreground text-xs tabular-nums">
                {formatDate(row.createdAt)}
            </TableCell>
            <TableCell className="text-muted-foreground text-xs tabular-nums">
                {row.acceptedAt ? formatDate(row.acceptedAt) : '—'}
            </TableCell>
            <TableCell className="text-right">
                {row.status === 'pending' ? (
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={revoking}
                        onClick={() => onRevoke(row.id)}
                        className="h-7 px-2.5 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 border-red-100"
                    >
                        {revoking ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Revoke'}
                    </Button>
                ) : (
                    <span className="text-muted-foreground/50 text-xs">—</span>
                )}
            </TableCell>
        </TableRow>
    )
}

const STATUS_BADGE: Record<'pending' | 'accepted', string> = {
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    accepted: 'bg-emerald-50 text-emerald-700 border-emerald-200',
}

function StatusBadge({ status }: { status: 'pending' | 'accepted' }) {
    return (
        <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium capitalize ${STATUS_BADGE[status]}`}
        >
            {status}
        </span>
    )
}

function FilterPill({
    active,
    onClick,
    children,
}: {
    active: boolean
    onClick: () => void
    children: React.ReactNode
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`px-2.5 h-7 rounded-full text-xs transition-colors border ${
                active
                    ? 'bg-foreground text-background border-foreground'
                    : 'bg-background text-foreground border-border hover:bg-muted'
            }`}
        >
            {children}
        </button>
    )
}

function formatDate(iso: string) {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    const now = new Date()
    const diffMs = now.getTime() - d.getTime()
    const diffMin = Math.floor(diffMs / 60000)
    if (diffMin < 1) return 'just now'
    if (diffMin < 60) return `${diffMin}m ago`
    const diffHr = Math.floor(diffMin / 60)
    if (diffHr < 24) return `${diffHr}h ago`
    const diffD = Math.floor(diffHr / 24)
    if (diffD < 7) return `${diffD}d ago`
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}
